import type { ReactElement } from 'react';
import { useState } from 'react';
import type { AdminMenuItemType, AdminMenuItemTypes } from '@cookspace-shared-contracts/core';
import { Plus } from 'lucide-react';

import {
    useCreateAdminMenuItemTypeMutation,
    useDeleteAdminMenuItemTypeMutation,
    useListAdminMenuItemTypesQuery,
    useReorderAdminMenuItemTypesMutation,
    useUpdateAdminMenuItemTypeMutation
} from '@/api/menuItemTypesApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { MenuItemTypeFormResult } from '@/components/MenuItemTypeEditModal';
import { MenuItemTypeEditModal } from '@/components/MenuItemTypeEditModal';
import type { MenuItemTypeReorderPayload } from '@/components/MenuItemTypeTree';
import { MenuItemTypeTree } from '@/components/MenuItemTypeTree';
import type { MenuItemTypeTreeNode } from '@/lib/buildMenuItemTypeTree';
import { getMenuItemTypeErrorMessage } from '@/lib/menuItemTypeErrors';

export function MenuItemTypesPage(): ReactElement {
    const { data, isLoading, isError } = useListAdminMenuItemTypesQuery();

    const [createType, { isLoading: isCreating }] = useCreateAdminMenuItemTypeMutation();
    const [updateType, { isLoading: isUpdating }] = useUpdateAdminMenuItemTypeMutation();
    const [deleteType] = useDeleteAdminMenuItemTypeMutation();
    const [reorderTypes] = useReorderAdminMenuItemTypesMutation();

    const [isEditOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<AdminMenuItemType | null>(null);
    const [creatingUnderParent, setCreatingUnderParent] = useState<AdminMenuItemType | null>(
        null
    );
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<AdminMenuItemType | null>(null);

    const items = data?.items ?? [];

    // Унаследованный возрастной порог для модалки: при создании — от
    // выбранного родителя, при редактировании — от родителя узла
    // (effectiveMinAge — денорм, считает только сервер).
    let modalParentEffectiveMinAge = 0;
    if (creatingUnderParent !== null) {
        modalParentEffectiveMinAge = creatingUnderParent.effectiveMinAge;
    } else if (editing !== null && editing.parentId !== null) {
        modalParentEffectiveMinAge =
            items.find((i) => i.id === editing.parentId)?.effectiveMinAge ?? 0;
    }

    const openCreateRoot = (): void => {
        setEditing(null);
        setCreatingUnderParent(null);
        setSubmitError(null);
        setEditOpen(true);
    };

    const openCreateChild = (parent: MenuItemTypeTreeNode): void => {
        setEditing(null);
        setCreatingUnderParent(parent);
        setSubmitError(null);
        setEditOpen(true);
    };

    const openEdit = (node: MenuItemTypeTreeNode): void => {
        setEditing(node);
        setCreatingUnderParent(null);
        setSubmitError(null);
        setEditOpen(true);
    };

    const closeEdit = (): void => {
        setEditOpen(false);
        setEditing(null);
        setCreatingUnderParent(null);
        setSubmitError(null);
    };

    const submitForm = async (values: MenuItemTypeFormResult): Promise<void> => {
        setSubmitError(null);
        try {
            if (editing !== null) {
                // PATCH-body `.strict()`: minAge шлём ТОЛЬКО при включённом
                // флаге (false + явный minAge — контрактное противоречие,
                // сервер нормализует minAge=null сам).
                const body: AdminMenuItemTypes.UpdateAdminMenuItemTypeRequest = {
                    name: values.name,
                    slug: values.slug,
                    sortOrder: values.sortOrder,
                    isActive: values.isActive,
                    isAgeRestricted: values.isAgeRestricted
                };
                if (values.isAgeRestricted && values.minAge !== null) {
                    body.minAge = values.minAge;
                }
                await updateType({ id: editing.id, ...body }).unwrap();
            } else {
                // contentDisplayMode всегда 'standard' — селект-задел P-8
                // задизейблен (строгий режим показа — вне MVP).
                const body: AdminMenuItemTypes.CreateAdminMenuItemTypeRequest = {
                    name: values.name,
                    slug: values.slug,
                    sortOrder: values.sortOrder,
                    parentId: creatingUnderParent !== null ? creatingUnderParent.id : null,
                    isAgeRestricted: values.isAgeRestricted,
                    contentDisplayMode: 'standard'
                };
                if (values.isAgeRestricted && values.minAge !== null) {
                    body.minAge = values.minAge;
                }
                await createType(body).unwrap();
            }
            closeEdit();
        } catch (error) {
            setSubmitError(getMenuItemTypeErrorMessage(error, 'Не удалось сохранить узел'));
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteType({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch (error) {
            setPendingDelete(null);
            setPageError(
                getMenuItemTypeErrorMessage(error, 'Не удалось деактивировать узел')
            );
        }
    };

    const handleReorder = async (payload: MenuItemTypeReorderPayload): Promise<void> => {
        try {
            await reorderTypes(payload).unwrap();
        } catch (error) {
            setPageError(getMenuItemTypeErrorMessage(error, 'Не удалось изменить порядок'));
        }
    };

    const handleMove = async (
        nodeId: string,
        newParentId: string | null
    ): Promise<void> => {
        try {
            await updateType({ id: nodeId, parentId: newParentId }).unwrap();
        } catch (error) {
            setPageError(getMenuItemTypeErrorMessage(error, 'Не удалось переместить узел'));
        }
    };

    const hasActiveChildren =
        pendingDelete !== null &&
        items.some((i) => i.parentId === pendingDelete.id && i.isActive);
    const deleteWarning =
        pendingDelete !== null && (pendingDelete.usageCount > 0 || hasActiveChildren)
            ? ' Внимание: узел используется группами кафе или имеет активные дочерние узлы — сервер откажет в деактивации.'
            : '';

    return (
        <div data-testid="menu-item-types-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Дерево типов меню</h1>
                <button
                    type="button"
                    onClick={openCreateRoot}
                    data-testid="menu-item-type-create-root-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать корневой узел
                </button>
            </div>

            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Системные типы меню (Еда / Напитки / Кальян…) — кафе выбирает тип для
                каждой группы блюд. Возрастное ограничение узла наследуется вниз всеми
                потомками: предок 18+ означает 18+ для всего поддерева. Перетащите узел
                на другой — он станет дочерним; перетащите внутри одной группы —
                измените порядок.
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="menu-item-types-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="menu-item-types-error">
                    Не удалось загрузить типы меню
                </div>
            )}

            {!isLoading && !isError && (
                <div className="rounded border border-gray-200 bg-white p-2">
                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Типы меню не созданы. Нажмите «Создать корневой узел».
                        </div>
                    )}
                    {items.length > 0 && (
                        <MenuItemTypeTree
                            items={items}
                            onEdit={openEdit}
                            onDelete={(node) => setPendingDelete(node)}
                            onAddChild={openCreateChild}
                            onReorder={(payload) => {
                                void handleReorder(payload);
                            }}
                            onMove={(nodeId, parentId) => {
                                void handleMove(nodeId, parentId);
                            }}
                            draggable
                        />
                    )}
                </div>
            )}

            <MenuItemTypeEditModal
                isOpen={isEditOpen}
                editing={editing}
                parent={creatingUnderParent}
                parentEffectiveMinAge={modalParentEffectiveMinAge}
                isSaving={isCreating || isUpdating}
                submitError={submitError}
                onClose={closeEdit}
                onSubmit={(values) => {
                    void submitForm(values);
                }}
            />

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Деактивировать узел"
                message={
                    pendingDelete
                        ? `Деактивировать узел «${pendingDelete.name}»? Кафе перестанут видеть его при выборе типа группы.${deleteWarning}`
                        : ''
                }
                confirmLabel="Деактивировать"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
